import { useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl';
import { ExpressCheckoutElement } from '@stripe/react-stripe-js';
import { Button } from '../common/Button';
import CardFormFooter from '../CardFormScreen/CardFormFooter';
import SelectedPlanBlock, { type SelectedPlanBlockProps } from '../common/SelectedPlanBlock';
import NarrowColumnContainer from '../NarrowColumnContainer';
import Layout from '../Layout';

import styles from './PaymentMethodsScreen.module.scss';

import { type OnExpressCheckoutConfirm, useExpressCheckout } from '@/utils/stripe/expressCheckoutHooks';
import { type Plan } from '@/utils/mapProductObject';
import AnalyticsEventHandler from '@/services/analytics/AnalyticsEventHandler';
import Checkbox from '@/components/common/Checkbox';
import Loader from '@/components/common/Loader';

const calculateTrialReminderDate = (plan: Plan): number => {
  const dayOfTrialReminderDelta = 1;

  return Math.max(plan.trialDays - dayOfTrialReminderDelta, dayOfTrialReminderDelta);
};

const calculateSubscriptionStartDate = (plan: Plan): Date => {
  const date = new Date();

  date.setDate(date.getDate() + plan.trialDays);

  return date;
};

interface PaymentMethodsScreenProps {
  selectedPlanBlockData: SelectedPlanBlockProps;
  onBackButtonClick: () => void;
  onCardFormButtonClick: () => void;
  onExpressCheckoutConfirm: OnExpressCheckoutConfirm;
  isWebRoute?: boolean;
  isSubmitting?: boolean;
}

const PaymentMethodsScreen = ({
  selectedPlanBlockData,
  onBackButtonClick,
  onCardFormButtonClick,
  onExpressCheckoutConfirm,
  isWebRoute,
  isSubmitting,
}: PaymentMethodsScreenProps) => {
  const { selectedPlan } = selectedPlanBlockData;

  const { onConfirm } = useExpressCheckout(onExpressCheckoutConfirm);

  const [termsCheckboxInput, setTermsCheckboxInput] = useState(false);

  const isButtonsDisabled = isWebRoute && !termsCheckboxInput;

  useEffect(() => {
    AnalyticsEventHandler.trackSubInfoShown(selectedPlan.sku);
  }, []);

  return (
    <Layout>
      <NarrowColumnContainer onBackButtonClick={onBackButtonClick}>
        <SelectedPlanBlock {...selectedPlanBlockData} />

        {selectedPlan.hasTrial && (
          <div className={styles.stepper}>
            <div className={styles.connector} />

            <img
              src="/images/new-order/payment-steps/stepper-01.svg"
              width="40"
              height="40"
              alt=""
              className={styles.stepperIconFirst}
            />
            <div>
              <h4 className={styles.firstStepTitle}>
                <FormattedMessage
                  id="paymentMethods.firstStep.title"
                  defaultMessage="Now: Install the App and Get Instant Access"
                  description="Purchase timeline; day 1 - title"
                />
              </h4>
              <span>
                <FormattedMessage
                  id="paymentMethods.firstStep.subtitle"
                  defaultMessage="Secure up the 6 devices and explore all features"
                  description="Purchase timeline; day 1 - subtitle"
                />
              </span>
            </div>

            <img
              src="/images/new-order/payment-steps/stepper-02.svg"
              width="40"
              height="40"
              alt=""
              className={styles.stepperIconSecond}
            />
            <div>
              <h4>
                <FormattedMessage
                  id="paymentMethods.secondStep.title"
                  defaultMessage="Day {trialReminderDay, number}: Trial Reminder"
                  description="Purchase timeline; trial reminder - title"
                  values={{
                    trialReminderDay: calculateTrialReminderDate(selectedPlan),
                  }}
                />
              </h4>
              <span>
                <FormattedMessage
                  id="paymentMethods.secondStep.subtitle"
                  defaultMessage="We’ll send you an email/notification.{lineBreak}Cancel anytime in just 15 seconds."
                  description="Purchase timeline; trial reminder - subtitle"
                  values={{ lineBreak: <br /> }}
                />
              </span>
            </div>

            <img
              src="/images/new-order/payment-steps/stepper-03.svg"
              width="40"
              height="40"
              alt=""
              className={styles.stepperIconThird}
            />
            <div>
              <h4>
                <FormattedMessage
                  id="paymentMethods.thirdStep.title"
                  defaultMessage="Day {trialDuration}: Trial Ends"
                  description="Purchase timeline; trial end - title"
                  values={{ trialDuration: selectedPlan.trialDays }}
                />
              </h4>
              <span>
                <FormattedMessage
                  id="paymentMethods.thirdStep.subtitle"
                  defaultMessage="Your subscription will start on {subscriptionStartDate, date, ::MMM d}.{lineBreak}So you will be charged due to the provided payment method"
                  description="Purchase timeline; trial end - subtitle"
                  values={{ subscriptionStartDate: calculateSubscriptionStartDate(selectedPlan), lineBreak: <br /> }}
                />
              </span>
            </div>
          </div>
        )}

        {isWebRoute && (
          <Checkbox
            className={styles.termsCheckbox}
            checked={termsCheckboxInput}
            name="terms"
            label={
              <FormattedMessage
                id="paymentMethods.firstStep.termsCheckboxLabel"
                defaultMessage="I agree to the <termsLink>Terms of Service</termsLink> and <policyLink>Privacy Policy</policyLink>. I understand that my subscription will automatically renew at the end of each term using my selected payment method."
                description="Terms of Service and Privacy Policy checkbox label"
                values={{
                  termsLink: (chunks) => <a href="https://clearvpn.com/terms-of-service/">{chunks}</a>,
                  policyLink: (chunks) => <a href="https://clearvpn.com/privacy-policy/">{chunks}</a>,
                }}
              />
            }
            onChange={setTermsCheckboxInput}
          />
        )}

        <div className={styles.buttons}>
          {isSubmitting ? (
            <Loader />
          ) : (
            <>
              <Button className={styles.fakeColorButton} disabled={isButtonsDisabled} onClick={onCardFormButtonClick}>
                <FormattedMessage
                  id="paymentMethods.payByCard"
                  defaultMessage="Pay by card"
                  description="Pay by card button"
                />
              </Button>

              <ExpressCheckoutElement
                options={{
                  paymentMethods: {
                    applePay: 'always',
                    googlePay: 'always',
                    link: 'auto',
                  },
                  buttonHeight: 48,
                }}
                onConfirm={onConfirm}
              />
            </>
          )}
        </div>
      </NarrowColumnContainer>

      <CardFormFooter />
    </Layout>
  );
};

export default PaymentMethodsScreen;
